// common
import React, { useEffect, useRef, useState } from "react"
import clsx from "clsx"

// redux
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"

// components
import Button from "../button"
import { debounce } from "../../helpers/debounce"
import TextInputField from "../textInput"
import { X } from "react-feather"
import { useLanguage } from "@languages/index"

// Props Interface
interface Props {
  className?: any
  style?: any

  maxHeight?: any
  maxWidth?: any
  minWidth?: any

  modal?: any
  sheet?: any
  popup?: any

  setSheet?: (params?: any) => void
  closePopup?: () => void
  setPopup?: (params?: any) => void
  closeSheet?: () => void

  setModal?: any
  preferences?: any
  setPreferences?: any

  hideCloseButton?: boolean
  renderCustomCloseButton?: () => void
  preventCloseWithBackgroundDimmer?: boolean

  isCompact?: boolean

  onRequestClose?: any
}

const ModalContainer: React.FunctionComponent<Props> = (props: Props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.modal }

  const modalRef = useRef()
  const [opened, setIsOpened] = useState<any>()
  const [inputValue, setInputValue] = useState(undefined)
  const fadeOutDuration = 300

  const openModal = () => {
    debounce(() => {
      if (!opened) {
        setIsOpened(true)
      }
    }, 200)
  }

  const closeModal = () => {
    if (props.modal.onRequestClose) {
      props.modal.onRequestClose()
    }

    if (props.modal.preventClose) {
      return false
    }

    if (opened) {
      setIsOpened(false)
    }

    debounce(() => {
      props.setModal({ isVisible: false })
    }, fadeOutDuration)
  }

  useEffect(() => {
    if (props.modal.isVisible) openModal()
    // if (!props.modal.isVisible) closeModal();
  }, [props.modal.isVisible])

  useEffect(() => {
    if (props.modal.closeModal) {
      closeModal()
    }
  }, [props.modal.closeModal])

  if (!props.modal.isVisible) {
    return <></>
  }

  const renderCustomModal = () => {
    if (!props.modal.content) {
      return null
    }

    return props.modal.content()
  }

  const renderDefaultModal = () => {
    if (props.modal.content) {
      return null
    }

    const onConfirm = () => {
      if (props.modal.confirm) {
        const params = props.modal.input ? inputValue : null

        props.modal.confirm(params)
      }

      closeModal()
    }

    const onCancel = () => {
      if (props.modal.cancel) props.modal.cancel()
      closeModal()
    }

    const renderTitle = () => {
      if (!props.modal.title) {
        return null
      }

      return (
        <span
          className={clsx({
            "font-bold text-[22px] text-black whitespace-pre-wrap": true,
            "pt-3": props.modal.isCompact,
          })}
        >
          {props.modal.title}
        </span>
      )
    }

    const renderText = () => {
      return (
        <span className="text-[15px] text-black" style={{ whiteSpace: "pre-wrap" }}>
          {props.modal.text}
        </span>
      )
    }

    const renderInput = () => {
      if (!props.modal.input) {
        return null
      }

      return (
        <>
          <TextInputField
            bordered
            borderColor="rgba(0,0,0, 0.3)"
            defaultValue={props.modal?.input?.value}
            placeholder={props.modal.input.placeholder}
            onChangeText={(value) => {
              setInputValue(value)
              props.modal.input.onChange ? props.modal.input.onChange(value) : null
            }}
          />
        </>
      )
    }

    const windowBackgroundColor = "white"

    const renderModalWindow = () => {
      return (
        <div
          className={clsx({
            "flex flex-col justify-center align-center w-full": true,
            "p-8": !props.modal.isCompact,
            "py-5 px-6": props.modal.isCompact,
          })}
          style={{ backgroundColor: windowBackgroundColor }}
        >
          <div className="content flex flex-col">
            {props.modal.title ? renderTitle() : null}
            <div
              className={clsx({
                "flex flex-col": true,
                "py-5": !props.modal.isCompact,
                "py-2": props.modal.isCompact,
              })}
            >
              {renderText()}
            </div>
            {props.modal.input ? renderInput() : null}
          </div>
          <div className="buttons flex flex-row columns-2 mt-3 gap-1.5">
            {props.modal.cancel || props.modal.showCancelButton ? (
              <Button
                className={clsx([
                  "py-3 flex justify-center text-center text-base",
                  {
                    "min-w-[10rem]": !props.modal.isCompact,
                    "min-w-[9rem]": props.modal.isCompact,
                  }
                ])}
                borderColor={"BLACK"}
                text={props.modal.cancelButtonText ? props.modal.cancelButtonText : languageTexts.cancel}
                onClick={() => onCancel()}
              />
            ) : null}
            {!props.modal.hideConfirmButton ? (
              <Button
                className={clsx([
                  "py-3 flex justify-center text-center text-base",
                  {
                    "min-w-[10rem]": !props.modal.isCompact,
                    "min-w-[9rem]": props.modal.isCompact,
                  }
                ])}
                text={props.modal.confirmButtonText ? props.modal.confirmButtonText : languageTexts.confirm}
                textColor={"WHITE"}
                buttonColor={"BLACK"}
                onClick={() => onConfirm()}
              />
            ) : null}
          </div>
        </div>
      )
    }

    return renderModalWindow()
  }

  //   const shadowStyle = {
  //     boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  //   }

  const shadowStyle = {
    // boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  }

  return (
    <div
      className="modal-container flex flex-col justify-center items-center animate__animated animate__fadeIn"
      style={{
        zIndex: props.modal.zIndex ? props.modal.zIndex : null,
      }}
    >
      <div
        className={
          props.modal.className ? "flex modal relative " + props.modal.className : "flex modal relative max-w-lg"
        }
        style={{
          ...{
            maxHeight: props.modal.maxHeight ? props.modal.maxHeight : null,
            maxWidth: props.modal.maxWidth ? props.modal.maxWidth : "90vw",
            minWidth: props.preferences?.isMobile ? null : props.modal.minWidth ? props.modal.minWidth : "",
          },
          ...shadowStyle,
        }}
        ref={modalRef}
      >
        {renderCustomModal()}
        {renderDefaultModal()}

        {props.modal.hideCloseButton ? null : props.modal.renderCustomCloseButton ? (
          props.modal.renderCustomCloseButton(closeModal)
        ) : (
          <div
            className="absolute right-0 top-0 p-[30px] cursor-pointer"
            onClick={() => {
              closeModal()
            }}
          >
            <X strokeWidth={1} />
          </div>
        )}
      </div>

      <div
        className="background-dimmer"
        onClick={(e) => {
          if (props?.modal?.preventCloseWithBackgroundDimmer) {
            return false
          }

          closeModal()
          if (props.modal.cancel) props.modal.cancel()
          e.stopPropagation()
        }}
      />
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(ModalContainer)
